import * as React from 'react';
import type { PriorityLevel, ReportStatus } from '@ci-ville/shared';
import { cn } from '../lib/cn.js';

// Étiquettes sobres (voir .claude/skills/interface-sobre) : un point de couleur suivi du libellé
// en couleur de texte normale. Pas de pastille arrondie ni de fond teinté.

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

const DOT: Record<Exclude<BadgeTone, 'neutral'>, string> = {
  brand: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-destructive',
};

/** Point de couleur (className = classe de fond), à placer devant un libellé. */
export function Dot({ className, pulse }: { className?: string; pulse?: boolean }) {
  return (
    <span aria-hidden className="relative inline-flex size-2 shrink-0">
      {pulse && <span className={cn('absolute inset-0 animate-ping rounded-full opacity-60', className)} />}
      <span className={cn('relative size-2 rounded-full', className)} />
    </span>
  );
}

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

export function Badge({ className, tone = 'neutral', children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium',
        tone === 'neutral' ? 'text-muted-foreground' : 'text-foreground',
        className,
      )}
      {...props}
    >
      {tone !== 'neutral' && <Dot className={DOT[tone]} />}
      {children}
    </span>
  );
}

/** Étiquette encadrée (filtre actif, mot-clé) : cadre fin neutre, coins à peine arrondis. */
export function Tag({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-sm border border-border bg-background px-1.5 py-0.5 text-xs font-medium text-foreground',
        className,
      )}
      {...props}
    />
  );
}

// ── Étiquettes métier ───────────────────────────────────────────
const STATUS_DOT: Record<ReportStatus, string> = {
  OPEN: 'bg-status-open',
  IN_PROGRESS: 'bg-status-in-progress',
  RESOLVED: 'bg-status-resolved',
  REJECTED: 'bg-status-rejected',
};

const STATUS_LABELS: Record<ReportStatus, string> = {
  OPEN: 'Nouveau',
  IN_PROGRESS: 'En cours',
  RESOLVED: 'Résolu',
  REJECTED: 'Rejeté',
};

export function StatusBadge({ status, className }: { status: ReportStatus; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-foreground', className)}>
      <Dot className={STATUS_DOT[status]} />
      {STATUS_LABELS[status]}
    </span>
  );
}

const PRIORITY_DOT: Record<PriorityLevel, string> = {
  URGENT: 'bg-priority-urgent',
  HIGH: 'bg-priority-high',
  NORMAL: 'bg-priority-normal',
  LOW: 'bg-priority-low',
};

const PRIORITY_LABELS: Record<PriorityLevel, string> = {
  URGENT: 'Urgente',
  HIGH: 'Haute',
  NORMAL: 'Normale',
  LOW: 'Basse',
};

export function PriorityBadge({ level, score, className }: { level: PriorityLevel; score?: number; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-foreground',
        level === 'URGENT' ? 'font-semibold' : 'font-medium',
        className,
      )}
    >
      <Dot className={PRIORITY_DOT[level]} pulse={level === 'URGENT'} />
      {PRIORITY_LABELS[level]}
      {typeof score === 'number' && <span className="font-normal text-muted-foreground">{score}</span>}
    </span>
  );
}

/** Catégorie du catalogue : point de sa couleur, libellé en texte normal. */
export function CategoryBadge({ name, color, className }: { name: string; color?: string | null; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium text-foreground', className)}>
      <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ backgroundColor: color ?? 'hsl(var(--muted-foreground))' }} />
      {name}
    </span>
  );
}
